import type { CoverageStatus } from '@/modules/projects/components/StatementCoverage/types'
import type { MapLane, SplitMap } from './map'
import type { NewStatementPlace } from './moves'
import type { StatementView } from './types'

// One coverage state at a time. Everything that does not match fades instead of leaving the map,
// so the user keeps the shape of the whole file in view.
export type Highlight = CoverageStatus | null

export const fadedClass = (highlight: Highlight, of: CoverageStatus | null) =>
  highlight !== null && highlight !== of && 'opacity-30'

// The statement open in the column, wherever it shows on the map.
export const OPEN_RING = 'outline-2 -outline-offset-2 outline-neutral-900'

// What the user chose to look at. It does not change the file: it only decides which years are
// drawn, because a long file is hundreds of rows of months.
export interface MapFilters {
  fromYear: number | null
  toYear: number | null
}

export const NO_FILTERS: MapFilters = {
  fromYear: null,
  toYear: null,
}

export const anyFilterOn = (filters: MapFilters) =>
  filters.fromYear !== null || filters.toYear !== null

export const yearIsShown = (year: number, filters: MapFilters) =>
  (filters.fromYear === null || year >= filters.fromYear) &&
  (filters.toYear === null || year <= filters.toYear)

// A click shows that one year; a shift-click stretches the pick out to it.
export const filtersAfterPicking = (
  year: number,
  stretch: boolean,
  filters: MapFilters,
): MapFilters => {
  const anchor = filters.fromYear
  if (stretch && anchor !== null) {
    return { fromYear: Math.min(anchor, year), toYear: Math.max(anchor, year) }
  }
  const onlyThisYear = filters.fromYear === year && filters.toYear === year
  return onlyThisYear ? NO_FILTERS : { fromYear: year, toYear: year }
}

const yearsInsideTheSpan = (lane: MapLane, filters: MapFilters): MapLane => ({
  ...lane,
  years: lane.years.filter((row) => yearIsShown(row.year, filters)),
})

// An account with nothing in the picked years has nothing to show, so it leaves the map. An
// account with no dated statement at all stays, so pages can still be dropped on it.
const showsAnything = (lane: MapLane, filters: MapFilters) =>
  lane.years.length === 0 ||
  lane.years.some((row) => yearIsShown(row.year, filters))

export const filterMap = (map: SplitMap, filters: MapFilters): SplitMap => ({
  lanes: anyFilterOn(filters)
    ? map.lanes
        .filter((lane) => showsAnything(lane, filters))
        .map((lane) => yearsInsideTheSpan(lane, filters))
    : map.lanes,
  unplaced: map.unplaced,
})

// Set on the node under the carried pages, not in React state: see carry.ts.
export const DROPPING_ON =
  'data-[drop-over]:border-blue-600 data-[drop-over]:bg-blue-600 data-[drop-over]:font-medium data-[drop-over]:text-white data-[drop-over]:opacity-100'

// While pages are held the map keeps its colours, so only the place under the pointer turns blue.
export const TARGET_UNDER_POINTER =
  'hover:outline-2 hover:-outline-offset-2 hover:outline-blue-600'

export interface MapActions {
  onOpen: (view: StatementView) => void
  // Opens the statement with its pages showing at the bottom, where they can be moved.
  onEditRange: (view: StatementView) => void
  onDelete: (view: StatementView) => void
  onPutIn: (view: StatementView) => void
  onPutInNew: (place: NewStatementPlace) => void
  onOpenSegment: (segmentId: number) => void
}
